import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { bannersApiV2 } from '../services/api';
import { trackBannerView, trackBannerClick } from '../services/bannerTracker';
import { resolveBackendImage } from '../utils/brandLogo';

export default function BannerCarousel({ municipioSlug }) {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBanners();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioSlug]);

  const loadBanners = async () => {
    try {
      // Pass slug directly; api signature is list(municipioSlug: string).
      // Falsy slug lets the axios interceptor auto-inject from React Router.
      const data = await bannersApiV2.list(municipioSlug);
      setBanners(data);
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = useCallback(() => {
    if (banners.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const prevSlide = () => {
    if (banners.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 5 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleBannerClick = (banner) => {
    if (!banner.link) return;
    // Track click
    trackBannerClick('promo', banner.id, { municipioId: banner.municipio_id });
    if (banner.link_interno) {
      navigate(banner.link);
    } else {
      window.open(banner.link, '_blank', 'noopener,noreferrer');
    }
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || banners.length <= 1) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, banners.length, nextSlide]);

  // Track view — dispara quando slide corrente muda (dedup no tracker)
  useEffect(() => {
    if (banners.length === 0) return;
    const b = banners[currentIndex];
    if (b?.id) {
      trackBannerView('promo', b.id, { municipioId: b.municipio_id });
    }
  }, [currentIndex, banners]);

  // Don't render if no banners
  if (loading || banners.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full overflow-hidden" data-testid="banner-carousel">
      {/* Main Carousel Container */}
      <div 
        className="relative w-full aspect-[21/9] md:aspect-[3/1] overflow-hidden"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        {/* Slides */}
        <div 
          className="flex transition-transform duration-500 ease-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {banners.map((banner, index) => (
            <div 
              key={banner.id} 
              className="w-full flex-shrink-0 relative cursor-pointer"
              onClick={() => handleBannerClick(banner)}
              data-testid={`banner-slide-${index}`}
            >
              <img
                src={banner.imagem_url}
                alt={banner.titulo}
                className="w-full h-full object-cover"
              />
              {/* Gradient overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Banner Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
                <h2 className="text-xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                  {banner.titulo}
                </h2>
                {banner.descricao && (
                  <p className="text-white/90 text-sm md:text-base max-w-2xl line-clamp-2">
                    {banner.descricao}
                  </p>
                )}
                {banner.link && (
                  <span className="inline-block mt-3 text-sm font-medium text-white/80 hover:text-white">
                    Saiba mais →
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        {banners.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevSlide(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/40 rounded-full backdrop-blur-sm transition-all group"
              data-testid="banner-prev-btn"
            >
              <ChevronLeft className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextSlide(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/40 rounded-full backdrop-blur-sm transition-all group"
              data-testid="banner-next-btn"
            >
              <ChevronRight className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {banners.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-white w-8' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                data-testid={`banner-dot-${index}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
